import { useState, useEffect } from 'react';
import { getLeaderboard, getGameState } from '../services/api';
import { triggerToast } from '../components/Toast';
import AnimatedCounter from '../components/AnimatedCounter';
import BackButton from '../components/BackButton';
import { useLang, TOTAL_PEETHS } from '../i18n';

function formatNum(n) {
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, '') + ' लाख';
  return n.toLocaleString('en-IN');
}

// Directional chevron for the pagination buttons. Inherits the button's text
// colour via currentColor so it dims with the button when disabled.
function Chevron({ dir = 'left', size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path
        d={dir === 'left' ? 'M15 6 L9 12 L15 18' : 'M9 6 L15 12 L9 18'}
        stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

const RANK_LABELS = { 1: '1st', 2: '2nd', 3: '3rd' };
const MEDAL_COLORS = {
  1: { bg: 'rgba(255,215,0,0.15)', border: 'rgba(255,215,0,0.3)', text: '#FFD700' },
  2: { bg: 'rgba(192,192,192,0.12)', border: 'rgba(192,192,192,0.2)', text: '#C0C0C0' },
  3: { bg: 'rgba(205,127,50,0.12)', border: 'rgba(205,127,50,0.2)', text: '#CD7F32' },
};

export default function LeaderboardScreen({ user, isGuest, onBack }) {
  const { t } = useLang();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    async function load() {
      try {
        const [lbData, stateData] = await Promise.all([
          getLeaderboard().catch(() => ({ leaderboard: [] })),
          !isGuest ? getGameState().catch(() => null) : Promise.resolve(null),
        ]);
        const lb = lbData.leaderboard || [];
        setLeaderboard(lb);
        if (!isGuest) {
          // Prefer the user's actual position in the live list so "Your Rank"
          // always matches the row that's shown (and never disagrees with the
          // list for tied scores). Fall back to the server-computed rank only
          // when the user is outside the top 100.
          const mine = user && lb.find(e => e.user_id === user.user_id);
          setMyRank(mine ? mine.rank : (stateData?.rank ?? null));
        }
      } catch {
        triggerToast(t('leaderboard.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isGuest]); // eslint-disable-line react-hooks/exhaustive-deps

  const top3 = leaderboard.slice(0, 3);

  // Regular users see only the top-3 podium; the full list of all players is
  // admin-only. (When there are fewer than 3 entries there's no podium yet, so
  // show those few rows to everyone rather than leaving the board blank.)
  const isAdmin = !isGuest && user?.role === 'admin';
  const listSource = top3.length < 3
    ? leaderboard
    : (isAdmin ? leaderboard.slice(3) : []);

  // Client-side pagination over the (admin) list.
  const totalPages = Math.max(1, Math.ceil(listSource.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = listSource.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="screen" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div className="screen-header">
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <div className="screen-header-title">{t('leaderboard.title')}</div>
          <div className="screen-header-sub">{t('leaderboard.subtitle')}</div>
        </div>
      </div>

      {/* My Rank Banner */}
      {myRank && !isGuest && (
        <div className="lb-my-rank glass-card">
          <div className="lb-my-rank-label">{t('leaderboard.yourRank')}</div>
          <div className="lb-my-rank-value">{myRank}</div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: '16px' }}>
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="skeleton" style={{ height: 56, marginBottom: 8, borderRadius: 12 }} />
          ))}
        </div>
      )}

      {/* Leaderboard */}
      {!loading && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 24px' }}>
          {leaderboard.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" style={{ marginBottom: 12 }}>
                <path d="M6 9H4.5a2.5 2.5 0 010-5C7 4 7 7 7 7"/>
                <path d="M18 9h1.5a2.5 2.5 0 000-5C17 4 17 7 17 7"/>
                <path d="M4 22h16"/>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 19.24 7 20h10c0-.76-.85-1.25-2.03-1.79C14.47 17.98 14 17.55 14 17v-2.34"/>
                <path d="M18 9c0 4-3.13 7-6 7s-6-3-6-7V3h12v6z"/>
              </svg>
              <p style={{ fontFamily: 'var(--font-hindi)', fontSize: 16, color: 'var(--cream)', opacity: 0.7 }}>
                {t('leaderboard.noData')}
              </p>
            </div>
          )}

          {/* Top 3 Podium */}
          {top3.length >= 3 && (
            <div className="podium">
              {[top3[1], top3[0], top3[2]].map((entry, idx) => {
                const rank = [2, 1, 3][idx];
                const mc = MEDAL_COLORS[rank];
                const isMe = user && entry.user_id === user.user_id;
                return (
                  <div key={entry.user_id}
                    className={`podium-item podium-${rank} ${isMe ? 'lb-entry-me' : ''}`}
                  >
                    <div className="podium-medal" style={{ color: mc.text }}>
                      {RANK_LABELS[rank]}
                    </div>
                    <div className="podium-avatar" style={{ borderColor: mc.text }}>
                      {entry.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="podium-name">{entry.name}{isMe ? ` (${t('leaderboard.you')})` : ''}</div>
                    <div className="podium-count">{formatNum(entry.total_ram_count)}</div>
                    <div className="podium-peeths">{t('leaderboard.peethsCount', { n: entry.peeths_visited, total: TOTAL_PEETHS })}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full player list — admin only (empty for regular users) */}
          {pageItems.map((entry, idx) => {
            const rank = entry.rank;
            const isMe = user && entry.user_id === user.user_id;
            return (
              <div key={entry.user_id}
                className={`lb-entry ${isMe ? 'lb-entry-me' : ''}`}
                style={{ animationDelay: `${idx * 0.04}s`, '--i': idx }}
              >
                <div className="lb-rank">
                  {rank}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="lb-name">
                    {entry.name} {isMe && `(${t('leaderboard.you')})`}
                  </div>
                  <div className="lb-peeths">
                    {t('leaderboard.peethsCount', { n: entry.peeths_visited, total: TOTAL_PEETHS })}
                  </div>
                </div>
                <div className="lb-count">
                  <AnimatedCounter value={entry.total_ram_count} duration={600} />
                  <div className="lb-count-label">{t('common.ramChants')}</div>
                </div>
              </div>
            );
          })}

          {/* Pagination — large, calm controls; only shown when needed */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 14, padding: '18px 0 6px',
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                aria-label={t('leaderboard.prevPage')}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 48, height: 48, padding: 0, borderRadius: '50%',
                  border: '1.5px solid rgba(201,130,26,0.45)',
                  background: safePage <= 1 ? 'rgba(255,248,232,0.4)' : 'linear-gradient(135deg, rgba(230,81,0,0.16), rgba(201,130,26,0.22))',
                  color: safePage <= 1 ? 'var(--text-soft)' : 'var(--saffron-dark)',
                  cursor: safePage <= 1 ? 'default' : 'pointer',
                  opacity: safePage <= 1 ? 0.45 : 1, transition: 'all 0.2s',
                }}
              >
                <Chevron dir="left" size={20} />
              </button>
              <span style={{
                fontFamily: 'var(--font-display)', fontSize: 14,
                color: 'var(--saffron)', whiteSpace: 'nowrap', minWidth: 84, textAlign: 'center',
              }}>
                {t('leaderboard.pageOf', { page: safePage, total: totalPages })}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                aria-label={t('leaderboard.nextPage')}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 48, height: 48, padding: 0, borderRadius: '50%',
                  border: '1.5px solid rgba(201,130,26,0.45)',
                  background: safePage >= totalPages ? 'rgba(255,248,232,0.4)' : 'linear-gradient(135deg, rgba(230,81,0,0.16), rgba(201,130,26,0.22))',
                  color: safePage >= totalPages ? 'var(--text-soft)' : 'var(--saffron-dark)',
                  cursor: safePage >= totalPages ? 'default' : 'pointer',
                  opacity: safePage >= totalPages ? 0.45 : 1, transition: 'all 0.2s',
                }}
              >
                <Chevron dir="right" size={20} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
